import { useEffect, useMemo } from 'react';

import {
  faCheckCircle,
  faClock,
  faPlusCircle,
  faTimesCircle,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import { endOfDay, isValid, startOfDay } from 'date-fns';
import { useAtom, useAtomValue } from 'jotai';
import { uniqBy } from 'lodash';
import { useInView } from 'react-intersection-observer';

import { useGetZuubInsuranceList } from '@/react-query/useGetZuubInsuranceList';
import { SparklesIcon } from '@/svgs/gradientIcons/sparklesIcon';

import { SummaryCard } from '../../../common-components/elements/SummaryCard/SummaryCard';
import { useAppointments } from '../../../react-query/useAppointments';
import { useDebounce } from '../../../utils/hooks/useDebounce';
import { useSearchParams } from '../../../utils/hooks/useSearchParams';
import { searchAtom, selectDateAtom } from '../../patientList/atoms';
import { PatientListPageLayout } from '../../patientList/PatientListPageLayout';
import { PatientListPageLayoutContextMap } from '../../patientList/types';
import { insuranceStatusFilterAtom, selectedCardAtom } from '../atoms';
import {
  calculateSummaryMetricsAndFilterAppointments,
  createZuubDataMap,
  getAppointmentInsuranceStatus,
} from '../helpers';

import { AutoVerifyTable } from './AutoVerifyTable';

export const AutoVerifyPage = () => {
  // state
  const search = useAtomValue(searchAtom);
  const [selectedDate, selectDate] = useAtom(selectDateAtom);
  const [insuranceStatusFilter] = useAtom(insuranceStatusFilterAtom);
  const [selectedCard, setSelectedCard] = useAtom(selectedCardAtom);

  // hooks
  const { t } = useLingui();
  const searchParams = useSearchParams();
  const { debouncedValue: debouncedSearch } = useDebounce(search);
  const { ref: bottomAppointmentRef, inView: isBottomAppointmentInView } =
    useInView();

  const isSearchValid = debouncedSearch.length > 2;

  const today = useMemo(() => new Date(), []);

  // Always have a default date range to ensure we get data
  let endDate: Date | undefined = endOfDay(today);
  let startDate: Date | undefined = startOfDay(today);
  let imagingStartDate: Date | undefined;
  let imagingEndDate: Date | undefined;

  if (selectedDate) {
    startDate = startOfDay(selectedDate);
    endDate = endOfDay(selectedDate);
  }

  // Clear date filters when searching to allow search across all dates
  if (isSearchValid) {
    endDate = undefined;
    startDate = undefined;
  }

  const appointmentArgs = {
    appointmentOpts: {
      startDate,
      endDate,
      imagingStartDate,
      imagingEndDate,
      onlyImaging: false,
    },
    patientOpts: {
      name: isSearchValid ? debouncedSearch : undefined,
    },
    meta: {
      sort: {
        appointmentOn: 'ASC' as const,
      },
    },
  };

  const {
    data: appointmentData,
    isLoading: isLoadingAppointments,
    isFetchingNextPage,
    fetchNextPage,
  } = useAppointments(appointmentArgs);

  // Same as what Huddle uses
  const appointments = useMemo(() => {
    if (!appointmentData?.pages) return [];
    const allAppointments = appointmentData.pages.flatMap(
      (page: any) => page.data,
    );
    const uniqueAppointments = uniqBy(allAppointments, 'appointment.unifiedId');
    return uniqueAppointments;
  }, [appointmentData?.pages]);

  // Extract unified IDs from appointments for Zuub API call
  const unifiedIds = useMemo(() => {
    return appointments.map((appointment) => appointment.appointment.unifiedId);
  }, [appointments]);

  // Fetch Zuub insurance data for all appointments
  const { data: zuubData } = useGetZuubInsuranceList({
    unifiedIds,
    enabled: unifiedIds.length > 0,
  });

  // Create a map of unifiedAppointmentId to Zuub patient data for quick lookup
  const zuubDataMap = useMemo(() => createZuubDataMap(zuubData), [zuubData]);

  // Apply insurance status filter
  const filteredAppointments = useMemo(() => {
    if (!appointments) return [];

    let filtered = appointments;

    // Apply insurance status filter
    if (insuranceStatusFilter && insuranceStatusFilter !== 'all') {
      filtered = appointments.filter((appointment) => {
        const appointmentStatus = getAppointmentInsuranceStatus(
          appointment,
          zuubDataMap,
        );
        return appointmentStatus === insuranceStatusFilter;
      });
    }

    return filtered;
  }, [appointments, insuranceStatusFilter, zuubDataMap]);

  // Calculate summary metrics and filter appointments for table display
  const { summaryMetrics, filteredAppointmentsForTable } = useMemo(
    () =>
      calculateSummaryMetricsAndFilterAppointments(
        filteredAppointments,
        selectedCard,
        zuubDataMap,
      ),
    [filteredAppointments, selectedCard, zuubDataMap],
  );

  const { nextCursor } =
    appointmentData?.pages[appointmentData.pages.length - 1] || {};

  // useEffects
  useEffect(() => {
    const selectedDateParam = searchParams.get('selectedDateParam');

    if (selectedDateParam && isValid(new Date(selectedDateParam))) {
      selectDate(new Date(selectedDateParam));
      return;
    }

    // Only set to today if there's no selected date in global state AND no active search
    // This prevents the date from resetting when user is searching
    if (!selectedDate && !search) {
      selectDate(today);
    }
  }, [selectDate, today, searchParams, selectedDate, search]);

  // Auto-return to today when search is cleared and no date is selected
  // But only if the user isn't actively searching
  useEffect(() => {
    if (!search && !selectedDate) {
      selectDate(today);
    }
  }, [search, selectedDate, selectDate, today]);

  useEffect(() => {
    if (
      !!appointmentData &&
      appointmentData.pages.length > 0 &&
      isBottomAppointmentInView &&
      nextCursor &&
      appointmentData?.pages.length &&
      appointmentData?.pages.length <= 20
    ) {
      fetchNextPage();
    }
  }, [
    appointmentData,
    isBottomAppointmentInView,
    nextCursor,
    appointmentData?.pages.length,
    fetchNextPage,
  ]);

  return (
    <PatientListPageLayout
      context={PatientListPageLayoutContextMap.AutoVerify}
      allAppointments={filteredAppointments}
      isLoadingAppointments={isLoadingAppointments}
      isLoadingVisitSummaries={false}
    >
      <div className="vai-flex vai-flex-col vai-h-full vai-overflow-hidden">
        {/* Summary Cards */}
        {!isLoadingAppointments && filteredAppointments.length > 0 && (
          <div className="vai-w-full vai-flex vai-gap-4 vai-mb-6 vai-justify-between vai-flex-shrink-0">
            {/* "All Patients" card */}
            <SummaryCard
              icon={<SparklesIcon className="vai-w-6 vai-h-6" />}
              value={summaryMetrics.total}
              label={t`ALL PATIENTS`}
              description={
                summaryMetrics.total === 1
                  ? t`patient scheduled for treatment`
                  : t`patients scheduled for treatment`
              }
              color="blue"
              isSelected={selectedCard === 'all'}
              onClick={() => setSelectedCard('all')}
              testId="auto-verify-summary-card"
            />

            {/* "Pending" card */}
            <SummaryCard
              icon={
                <FontAwesomeIcon
                  icon={faClock}
                  className="vai-text-gray-600 vai-w-6 vai-h-6"
                />
              }
              value={summaryMetrics.pending}
              label={t`PENDING`}
              description={
                summaryMetrics.pending === 1
                  ? t`verification in progress`
                  : t`verifications in progress`
              }
              color="grey"
              isSelected={selectedCard === 'pending'}
              onClick={() => setSelectedCard('pending')}
              testId="auto-verify-summary-card"
            />

            {/* "Failed" card */}
            <SummaryCard
              icon={
                <FontAwesomeIcon
                  icon={faTimesCircle}
                  className="vai-text-red-600 vai-w-6 vai-h-6"
                />
              }
              value={summaryMetrics.failed}
              label={t`FAILED`}
              description={
                summaryMetrics.failed === 1
                  ? t`verification requires manual confirmation`
                  : t`verifications require manual confirmation`
              }
              color="red"
              isSelected={selectedCard === 'failed'}
              onClick={() => setSelectedCard('failed')}
              testId="auto-verify-summary-card"
            />

            {/* "Verified" card */}
            <SummaryCard
              icon={
                <FontAwesomeIcon
                  icon={faCheckCircle}
                  className="vai-text-green-600 vai-w-6 vai-h-6"
                />
              }
              value={summaryMetrics.verified}
              label={t`VERIFIED`}
              description={
                summaryMetrics.verified === 1
                  ? t`verification ran successfully`
                  : t`verifications ran successfully`
              }
              color="green"
              isSelected={selectedCard === 'verified'}
              onClick={() => setSelectedCard('verified')}
              testId="auto-verify-summary-card"
            />
          </div>
        )}

        <div className="vai-flex-1 vai-min-h-0 vai-overflow-hidden">
          {isLoadingAppointments ? (
            <div className="vai-flex vai-justify-center vai-items-center vai-h-full">
              <div className="vai-border-4 vai-border-gray-200 vai-border-t-blue-500 vai-rounded-full vai-w-10 vai-h-10 vai-animate-spin" />
            </div>
          ) : filteredAppointmentsForTable.length === 0 ? (
            <div className="vai-w-full vai-text-center vai-p-8 vai-text-zinc-700">
              <h3 className="vai-text-lg vai-font-medium vai-mb-2">
                <Trans>No patients found</Trans>
              </h3>
              <p className="vai-text-sm vai-text-zinc-500">
                <Trans>
                  There are no patients matching your current filter. Try
                  selecting a different status or check back later.
                </Trans>
              </p>
            </div>
          ) : (
            <>
              <AutoVerifyTable
                appointments={filteredAppointmentsForTable}
                zuubDataMap={zuubDataMap}
                isSearchActive={isSearchValid}
              />
              <div ref={bottomAppointmentRef} />
              {isFetchingNextPage &&
                filteredAppointmentsForTable?.length !== 0 && (
                  <div className="vai-flex vai-justify-center vai-p-4">
                    <div className="vai-border-4 vai-border-gray-200 vai-border-t-blue-500 vai-rounded-full vai-w-8 vai-h-8 vai-animate-spin" />
                  </div>
                )}
            </>
          )}
        </div>
      </div>
    </PatientListPageLayout>
  );
};
